import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { isEqual, range } from 'lodash-es';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useOverlayContext, Button, Card, IconButton, InfoIcon, MenuItem, Page, Select, TextButton, TextField, Typography, Checkbox, WithInfoIcon } from 'seagull';
import { PlusCircle, Trash } from 'seagull/icons';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { getTestIds } from 'seagull/utils/TestIds';
import { accountantAccessInfoTooltip } from '~/business-logic/accountantAccessLogic';
import { getAllAutoApprovalDays } from '~/business-logic/autoApprovalLogic';
import IntroVideoField from '~/components/AgreementStudioPage/AgreementStudioModal/IntroVideo/IntroVideoField';
import { normalizeVariableLabel, validateTemplateVariableLabel, validateTemplateVariableDefaultValue } from '~/components/AgreementStudioPage/TemplateVariables/TemplateVariables.utils';
import { LegalTermsDropdown } from '~/components/LegalTermsDropdown/LegalTermsDropdown';
import { NavigationGuardModal } from '~/components/NavigationGuardModal';
import { businessSettingsDefaults, daysOfWeek, netTermsOptions } from '~/constants';
import { useStoreActions } from '~/contexts';
import { useLegalTerms } from '~/hooks/reactQuery/queries/useLegalTerms';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useLocaleKeys } from '~/i18n';
import { sendErrReport } from '~/infra/utils';
import { BillingDayOfMonthModel, BillingDayOfMonthType, approvalMethodNoticePeriodModel } from '~/models';
import { makeExpirationDate } from '~/models/expiration-date';
import { RequestAccountantAccess, requestAccountantAccessModel } from '~/models/request-accountant-access';
import { selectors } from '~/store/state-selectors';
import { useStore } from '~/store/store-context';
import { StartBillingOnEnum } from '~/types/api.model';
import pluralize from '~/utils/pluralize';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import IntroductoryMessageEditor from './IntroductoryMessageEditor/IntroductoryMessageEditor';
import styles from './ProposalDefaults.module.scss';
const testPrefix = 'settings-business-settings';
const updateLoader = 'updateBusinessSettings';
var FormFieldType = /*#__PURE__*/ function(FormFieldType) {
    FormFieldType["Input"] = "input";
    FormFieldType["Heading"] = "heading";
    FormFieldType["Separator"] = "separator";
    FormFieldType["LegalTermsUpload"] = "legalTermsUpload";
    return FormFieldType;
}(FormFieldType || {});
const formId = 'business-settings-form';
export const { ProposalDefaultsTID } = getTestIds();
export default function ProposalDefaults() {
    var _ref;
    var _worksAt_settings, _worksAt_settings1, _worksAt_settings2, _worksAt_settings3, _worksAt_settings4, _worksAt_settings5, _worksAt_settings6, _worksAt_settings7, _worksAt_settings8, _worksAt_settings9, _worksAt_settings10, _worksAt_settings11, _worksAt_settings12, _worksAt_settings13, _worksAt_settings14, _worksAt_settings15, _worksAt_settings16, _worksAt_settings17, _worksAt_settings18, _worksAt_settings19, _worksAt_settings20;
    const worksAt = useStore(selectors.user.business);
    const { api, setWorksAt, fetchUserInfo } = useStoreActions();
    const { startLoader, stopLoader, showErrorToast } = useOverlayContext();
    const { legalTerms } = useLegalTerms({
        refreshOnHookMount: true,
        withLoader: true
    });
    const [isLegalTermsFieldDirty, setIsLegalTermsFieldDirty] = useState(false);
    const { t } = useLocaleKeys('pages.proposalDefaults');
    const { TEMPLATE_VARIABLES } = useFeatureFlags();
    const savedTemplateVariables = (_ref = worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings = worksAt.settings) === null || _worksAt_settings === void 0 ? void 0 : _worksAt_settings.templateVariables) !== null && _ref !== void 0 ? _ref : [];
    const [templateVariables, setTemplateVariables] = useState(savedTemplateVariables.map((v)=>_object_spread_props(_object_spread({}, v), {
            id: generateUniqueId('template-var')
        })));
    const isTemplateVariablesDirty = !isEqual(templateVariables.map((param)=>{
        let { label, defaultValue } = param;
        return {
            label,
            defaultValue
        };
    }), savedTemplateVariables);
    const updateVariableLabel = useCallback((index, label)=>{
        setTemplateVariables((prev)=>prev.map((v, i)=>i === index ? _object_spread_props(_object_spread({}, v), {
                    label
                }) : v));
    }, []);
    const updateVariableDefaultValue = useCallback((index, defaultValue)=>{
        setTemplateVariables((prev)=>prev.map((v, i)=>i === index ? _object_spread_props(_object_spread({}, v), {
                    defaultValue
                }) : v));
    }, []);
    const removeVariable = useCallback((index)=>{
        setTemplateVariables((prev)=>prev.filter((_, i)=>i !== index));
    }, []);
    const addVariable = useCallback(()=>{
        setTemplateVariables((prev)=>[
                ...prev,
                {
                    id: generateUniqueId('template-var'),
                    label: '',
                    defaultValue: ''
                }
            ]);
    }, []);
    useEffect(()=>{
        if (window.location.hash === '#custom-tags') {
            const section = document.getElementById('custom-tags-section');
            section === null || section === void 0 ? void 0 : section.scrollIntoView({
                behavior: 'smooth'
            });
        }
    }, []);
    const billingDayOfMonthOptions = range(1, 29).map((day)=>BillingDayOfMonthModel.make(day)).concat([
        BillingDayOfMonthModel.make()
    ]);
    const autoApprovalOptions = useMemo(()=>{
        return [
            approvalMethodNoticePeriodModel.makeManualApprovalMethodNoticePeriod()
        ].concat(getAllAutoApprovalDays().map(approvalMethodNoticePeriodModel.makeAutoApprovalMethodNoticePeriod));
    }, []);
    const startBillingOnMode = (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings1 = worksAt.settings) === null || _worksAt_settings1 === void 0 ? void 0 : _worksAt_settings1.startBillingOn) || businessSettingsDefaults.startBillingOn || StartBillingOnEnum.SkipBillingOnAcceptance;
    const selectedBillingDayOfMonth = (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings2 = worksAt.settings) === null || _worksAt_settings2 === void 0 ? void 0 : _worksAt_settings2.billingDayOfMonth) || businessSettingsDefaults.billingDayOfMonth;
    if (selectedBillingDayOfMonth.type === BillingDayOfMonthType.LastDayOfMonth) delete selectedBillingDayOfMonth['day'];
    const defaultValues = {
        legalTermsDocumentId: (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings3 = worksAt.settings) === null || _worksAt_settings3 === void 0 ? void 0 : _worksAt_settings3.legalTermsDocumentId) || businessSettingsDefaults.legalTermsDocumentId,
        disablePlaidLink: (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings4 = worksAt.settings) === null || _worksAt_settings4 === void 0 ? void 0 : _worksAt_settings4.disablePlaidLink) || businessSettingsDefaults.disablePlaidLink,
        netTerms: (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings5 = worksAt.settings) === null || _worksAt_settings5 === void 0 ? void 0 : _worksAt_settings5.netTerms) || businessSettingsDefaults.netTerms,
        billingDayOfMonth: selectedBillingDayOfMonth,
        billingDayOfWeek: (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings6 = worksAt.settings) === null || _worksAt_settings6 === void 0 ? void 0 : _worksAt_settings6.billingDayOfWeek) || businessSettingsDefaults.billingDayOfWeek,
        feeControls: {
            coverCreditCardFees: (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings7 = worksAt.settings) === null || _worksAt_settings7 === void 0 ? void 0 : _worksAt_settings7.feeControls.coverCreditCardFees) || businessSettingsDefaults.feeControls.coverCreditCardFees,
            coverDirectDebitFees: (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings8 = worksAt.settings) === null || _worksAt_settings8 === void 0 ? void 0 : _worksAt_settings8.feeControls.coverDirectDebitFees) || businessSettingsDefaults.feeControls.coverDirectDebitFees
        },
        amendmentsApprovalMethod: (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings9 = worksAt.settings) === null || _worksAt_settings9 === void 0 ? void 0 : _worksAt_settings9.amendmentsApprovalMethod) || businessSettingsDefaults.amendmentsApprovalMethod,
        invoicesApprovalMethod: (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings10 = worksAt.settings) === null || _worksAt_settings10 === void 0 ? void 0 : _worksAt_settings10.invoicesApprovalMethod) || businessSettingsDefaults.invoicesApprovalMethod,
        billUpfront: (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings11 = worksAt.settings) === null || _worksAt_settings11 === void 0 ? void 0 : _worksAt_settings11.billUpfront) || businessSettingsDefaults.billUpfront,
        automaticServicesPausedByDefault: (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings12 = worksAt.settings) === null || _worksAt_settings12 === void 0 ? void 0 : _worksAt_settings12.automaticServicesPausedByDefault) || businessSettingsDefaults.automaticServicesPausedByDefault,
        prorateFirstInvoice: (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings13 = worksAt.settings) === null || _worksAt_settings13 === void 0 ? void 0 : _worksAt_settings13.prorateFirstInvoice) || businessSettingsDefaults.prorateFirstInvoice,
        requestAccountantAccess: (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings14 = worksAt.settings) === null || _worksAt_settings14 === void 0 ? void 0 : _worksAt_settings14.requestAccountantAccess) || businessSettingsDefaults.requestAccountantAccess,
        introductoryMessages: {
            proposal: (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings15 = worksAt.settings) === null || _worksAt_settings15 === void 0 ? void 0 : _worksAt_settings15.introductoryMessages.proposal) || businessSettingsDefaults.introductoryMessages.proposal,
            invitation: (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings16 = worksAt.settings) === null || _worksAt_settings16 === void 0 ? void 0 : _worksAt_settings16.introductoryMessages.invitation) || businessSettingsDefaults.introductoryMessages.invitation
        },
        startBillingOn: (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings17 = worksAt.settings) === null || _worksAt_settings17 === void 0 ? void 0 : _worksAt_settings17.startBillingOn) || businessSettingsDefaults.startBillingOn,
        introVideo: (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings18 = worksAt.settings) === null || _worksAt_settings18 === void 0 ? void 0 : _worksAt_settings18.introVideo) || businessSettingsDefaults.introVideo,
        selectedTemplate: 'introductoryMessages.proposal',
        startPaused: startBillingOnMode === StartBillingOnEnum.StartPaused,
        billOnAcceptance: startBillingOnMode === StartBillingOnEnum.Earliest,
        expirationDate: (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings19 = worksAt.settings) === null || _worksAt_settings19 === void 0 ? void 0 : _worksAt_settings19.expirationDate) || businessSettingsDefaults.expirationDate,
        invoicePrefix: (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings20 = worksAt.settings) === null || _worksAt_settings20 === void 0 ? void 0 : _worksAt_settings20.invoicePrefix) || businessSettingsDefaults.invoicePrefix
    };
    delete defaultValues['defaultLegalTermsDocumentId'];
    delete defaultValues['defaultBillingDayOfMonth'];
    const { control, handleSubmit, formState, getValues, reset, setValue } = useForm({
        defaultValues,
        mode: 'onSubmit'
    });
    const [lastDayOptionDisabled, setLastDayOptionDisabled] = useState(getValues('billUpfront'));
    const billUpfront = useWatch({
        name: 'billUpfront',
        control
    });
    const startPaused = useWatch({
        name: 'startPaused',
        control
    });
    const billOnAcceptance = useWatch({
        name: 'billOnAcceptance',
        control
    });
    const prorateFirstInvoice = useWatch({
        name: 'prorateFirstInvoice',
        control
    });
    const startBillingOn = useWatch({
        name: 'startBillingOn',
        control
    });
    const introVideo = useWatch({
        name: 'introVideo',
        control
    });
    const billingDayOfMonth = useWatch({
        name: 'billingDayOfMonth.type',
        control
    });
    const legalTermsDocumentId = useWatch({
        name: 'legalTermsDocumentId',
        control
    });
    const [isProrationDisabled, setIsProrationDisabled] = useState(false);
    const [isStartPausedDisabled, setIsStartPausedDisabled] = useState(false);
    const [isBillOnAcceptanceDisabled, setIsBillOnAcceptanceDisabled] = useState(false);
    const billUpfrontErrorShown = billUpfront && billingDayOfMonth === BillingDayOfMonthType.LastDayOfMonth;
    useSetDocumentTitle(t.pageTitle());
    useEffect(()=>{
        if (!billUpfront && startBillingOn === StartBillingOnEnum.Earliest) {
            setValue('startBillingOn', StartBillingOnEnum.SkipBillingOnAcceptance);
        }
    }, [
        billUpfront,
        startBillingOn
    ]);
    useEffect(()=>{
        const isProrationInvalid = startPaused || !billOnAcceptance && billUpfront;
        setIsProrationDisabled(isProrationInvalid);
        setValue('prorateFirstInvoice', isProrationInvalid ? false : prorateFirstInvoice);
    }, [
        startPaused,
        billOnAcceptance,
        billUpfront,
        setValue,
        prorateFirstInvoice
    ]);
    useEffect(()=>{
        const isStartPausedInvalid = billUpfront && billOnAcceptance;
        setIsStartPausedDisabled(isStartPausedInvalid);
        setValue('startPaused', isStartPausedInvalid ? false : startPaused);
    }, [
        billOnAcceptance,
        billUpfront,
        setValue,
        startPaused
    ]);
    useEffect(()=>{
        const isBillOnAcceptanceInvalid = startPaused || !billUpfront;
        setIsBillOnAcceptanceDisabled(isBillOnAcceptanceInvalid);
        setValue('billOnAcceptance', isBillOnAcceptanceInvalid ? false : billOnAcceptance);
    }, [
        startPaused,
        billUpfront,
        setValue,
        billOnAcceptance
    ]);
    const selectedLegalTerms = useMemo(()=>{
        return legalTerms.find((lt)=>lt.id === legalTermsDocumentId);
    }, [
        legalTermsDocumentId,
        legalTerms
    ]);
    const fields = [
        {
            type: "heading",
            label: t.sections.generalSettings()
        },
        {
            type: "legalTermsUpload",
            label: t.fields.termsOfService()
        },
        {
            type: "input",
            name: 'amendmentsApprovalMethod',
            label: t.fields.amendmentsApproval.label(),
            tooltip: t.fields.amendmentsApproval.tooltip(),
            options: autoApprovalOptions.map((option)=>({
                    value: option,
                    label: approvalMethodNoticePeriodModel.isAutoApproval(option) ? t.options.enabledWithNoticeDays({
                        days: pluralize('day', option.auto.noticePeriodDays, true)
                    }) : t.options.disabled()
                }))
        },
        {
            type: "input",
            name: 'invoicesApprovalMethod',
            label: t.fields.invoicesApproval.label(),
            tooltip: t.fields.invoicesApproval.tooltip(),
            options: autoApprovalOptions.map((option)=>({
                    value: option,
                    label: approvalMethodNoticePeriodModel.isAutoApproval(option) ? t.options.enabledWithNoticeDays({
                        days: pluralize('day', option.auto.noticePeriodDays, true)
                    }) : t.options.disabled()
                }))
        },
        {
            type: "input",
            name: 'netTerms',
            label: t.fields.netTerms.label(),
            options: netTermsOptions.map((numOfDays)=>({
                    label: numOfDays === 0 ? t.fields.netTerms.onAcceptance() : pluralize('day', numOfDays, true),
                    value: numOfDays
                }))
        },
        {
            type: "input",
            name: 'feeControls.coverCreditCardFees',
            label: t.fields.creditCardFees.label(),
            options: [
                {
                    label: t.fields.creditCardFees.theClient(),
                    value: false
                },
                {
                    label: (worksAt === null || worksAt === void 0 ? void 0 : worksAt.name) || t.fields.creditCardFees.theVendor(),
                    value: true
                }
            ]
        },
        {
            type: "input",
            name: 'expirationDate',
            label: t.fields.expirationDate.label(),
            options: [
                {
                    label: t.fields.expirationDate.noExpiration(),
                    value: makeExpirationDate()
                },
                ...range(1, 91).map((day)=>({
                        label: day + '',
                        value: makeExpirationDate(day)
                    }))
            ]
        },
        {
            type: "input",
            name: 'requestAccountantAccess',
            label: t.fields.accountantAccess.label(),
            tooltip: accountantAccessInfoTooltip,
            isCheckbox: true,
            mapValue: (value)=>requestAccountantAccessModel.wasRequested(value),
            mapOnChange: (event)=>event.target.checked ? RequestAccountantAccess.Mandatory : RequestAccountantAccess.None
        },
        {
            type: "separator"
        },
        {
            type: "heading",
            label: t.sections.automaticServicesSettings()
        },
        {
            type: "input",
            name: 'billUpfront',
            label: t.fields.billUpfront.label(),
            options: [
                {
                    value: true,
                    label: t.fields.billUpfront.upfront()
                },
                {
                    value: false,
                    label: t.fields.billUpfront.endOfCycle()
                }
            ]
        },
        {
            type: "input",
            name: 'billingDayOfMonth',
            label: t.fields.billingDayOfMonth.label(),
            tooltip: t.fields.billingDayOfMonth.tooltip(),
            options: billingDayOfMonthOptions.map((option)=>({
                    label: option.formatDay(t.fields.billingDayOfMonth.lastDay()),
                    value: option,
                    disabled: option.isLast() && lastDayOptionDisabled
                })),
            error: billUpfrontErrorShown,
            helperText: billUpfrontErrorShown ? t.fields.billingDayOfMonth.error() : ''
        },
        {
            type: "input",
            name: 'billingDayOfWeek',
            label: t.fields.billingDayOfWeek.label(),
            tooltip: t.fields.billingDayOfWeek.tooltip(),
            options: daysOfWeek.map((value)=>({
                    value,
                    label: value
                }))
        },
        {
            type: "input",
            isCheckbox: true,
            name: 'startPaused',
            disabled: isStartPausedDisabled,
            label: t.fields.startPaused.label(),
            tooltip: t.fields.startPaused.tooltip(),
            options: [
                {
                    label: t.options.disabled(),
                    value: false
                },
                {
                    label: t.options.enabled(),
                    value: true
                }
            ]
        },
        {
            type: "input",
            isCheckbox: true,
            name: 'billOnAcceptance',
            disabled: isBillOnAcceptanceDisabled,
            label: t.fields.billOnAcceptance.label(),
            tooltip: t.fields.billOnAcceptance.tooltip(),
            options: [
                {
                    label: t.options.disabled(),
                    value: false
                },
                {
                    label: t.options.enabled(),
                    value: true
                }
            ]
        },
        {
            type: "input",
            isCheckbox: true,
            name: 'prorateFirstInvoice',
            label: t.fields.prorateFirstInvoice.label(),
            disabled: isProrationDisabled,
            tooltip: t.fields.prorateFirstInvoice.tooltip(),
            options: [
                {
                    label: t.options.disabled(),
                    value: false
                },
                {
                    label: t.options.enabled(),
                    value: true
                }
            ]
        },
        {
            type: "separator"
        }
    ];
    const onSubmit = async (values)=>{
        if (!worksAt) return;
        if (billUpfrontErrorShown) return;
        const hasValidationErrors = templateVariables.some((v)=>validateTemplateVariableLabel(v.label) !== null || v.label.trim().length > 0 && validateTemplateVariableDefaultValue(v.defaultValue) !== null);
        if (hasValidationErrors) return;
        startLoader(updateLoader);
        const params = _object_spread(_object_spread_props(_object_spread({}, values), {
            coverCreditCardFees: values.feeControls.coverCreditCardFees,
            startBillingOn: values.startPaused ? StartBillingOnEnum.StartPaused : values.billOnAcceptance ? StartBillingOnEnum.Earliest : StartBillingOnEnum.SkipBillingOnAcceptance
        }), TEMPLATE_VARIABLES ? {
            templateVariables: templateVariables.filter((v)=>v.label.trim().length > 0).reduce((acc, v)=>{
                const normalized = normalizeVariableLabel(v.label);
                if (!acc.some((existing)=>normalizeVariableLabel(existing.label) === normalized)) {
                    acc.push({
                        label: v.label.trim(),
                        defaultValue: v.defaultValue
                    });
                }
                return acc;
            }, [])
        } : {});
        delete params['selectedTemplate'];
        delete params['feeControls'];
        delete params['disablePlaidLink'];
        delete params['startPaused'];
        delete params['billOnAcceptance'];
        try {
            await api.vendor.updateBusinessSettings(worksAt.id, params);
            const settings = await api.vendor.getBusinessSettings(worksAt.id);
            if (settings) {
                var _settings_templateVariables;
                setWorksAt(Object.assign({}, worksAt, {
                    settings
                }));
                const persistedVariables = (_settings_templateVariables = settings.templateVariables) !== null && _settings_templateVariables !== void 0 ? _settings_templateVariables : [];
                setTemplateVariables(persistedVariables.map((v)=>_object_spread_props(_object_spread({}, v), {
                        id: generateUniqueId('template-var')
                    })));
            }
            setIsLegalTermsFieldDirty(false);
            reset(undefined, {
                keepValues: true
            });
        } catch (error) {
            showErrorToast(t.errors.failedToSave(), 'save-error-toast');
            sendErrReport(error);
        }
        stopLoader(updateLoader);
    };
    const onDeleteLegalTerm = async (legalTermsId)=>{
        const userInfo = await fetchUserInfo();
        if (legalTermsId === legalTermsDocumentId) {
            var _ref;
            var _userInfo_business_settings, _userInfo_business;
            const newLegalTermsDocumentId = (_ref = userInfo === null || userInfo === void 0 ? void 0 : (_userInfo_business = userInfo.business()) === null || _userInfo_business === void 0 ? void 0 : (_userInfo_business_settings = _userInfo_business.settings) === null || _userInfo_business_settings === void 0 ? void 0 : _userInfo_business_settings.legalTermsDocumentId) !== null && _ref !== void 0 ? _ref : businessSettingsDefaults.legalTermsDocumentId;
            setValue('legalTermsDocumentId', newLegalTermsDocumentId, {
                shouldValidate: true,
                shouldDirty: true
            });
        }
    };
    return /*#__PURE__*/ _jsxs(Page, {
        header: /*#__PURE__*/ _jsx(Typography, {
            variant: "h5",
            children: t.pageHeader()
        }),
        subheader: /*#__PURE__*/ _jsx(Typography, {
            children: t.pageSubheader()
        }),
        testId: testPrefix + '-screen',
        minWidth: "978px",
        actions: /*#__PURE__*/ _jsx(Button, {
            loading: formState.isSubmitting,
            testId: testPrefix + '-btn-save-changes',
            form: formId,
            type: "submit",
            children: t.saveChanges()
        }),
        skin: "secondary",
        children: [
            /*#__PURE__*/ _jsxs(Card, {
                autoHeight: true,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.titles
                    }),
                    /*#__PURE__*/ _jsxs("form", {
                        id: formId,
                        className: styles.form,
                        onSubmit: handleSubmit(onSubmit),
                        children: [
                            fields.map((fieldConfig, idx)=>{
                                if (fieldConfig.type === "input") {
                                    const { name, label, tooltip, options, error, helperText, isCheckbox, disabled, mapValue, mapOnChange } = fieldConfig;
                                    if (isCheckbox) {
                                        return /*#__PURE__*/ _jsx("div", {
                                            className: cn(styles.fieldWithTooltip, styles.checkboxField),
                                            children: /*#__PURE__*/ _jsx(Controller, {
                                                control: control,
                                                name: name,
                                                render: (_0)=>{
                                                    let { field: _ref } = _0, { onChange, value } = _ref, field = _object_without_properties(_ref, [
                                                        "onChange",
                                                        "value"
                                                    ]);
                                                    return tooltip ? /*#__PURE__*/ _jsx(WithInfoIcon, {
                                                        info: tooltip,
                                                        children: /*#__PURE__*/ _jsx(Checkbox, _object_spread_props(_object_spread({
                                                            testId: "".concat(testPrefix, "-").concat(name),
                                                            checked: mapValue ? mapValue(value) : value,
                                                            disabled: disabled,
                                                            onChange: (event)=>onChange(mapOnChange ? mapOnChange(event) : event)
                                                        }, field), {
                                                            children: /*#__PURE__*/ _jsx(Typography, {
                                                                skin: disabled ? 'disabled' : 'standard',
                                                                children: label
                                                            })
                                                        }), name)
                                                    }) : /*#__PURE__*/ _jsx(Checkbox, _object_spread_props(_object_spread({
                                                        testId: "".concat(testPrefix, "-").concat(name),
                                                        checked: value,
                                                        disabled: disabled,
                                                        onChange: onChange
                                                    }, field), {
                                                        children: /*#__PURE__*/ _jsx(Typography, {
                                                            skin: disabled ? 'disabled' : 'standard',
                                                            children: label
                                                        })
                                                    }), name);
                                                }
                                            })
                                        }, name);
                                    } else {
                                        return /*#__PURE__*/ _jsxs(Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: styles.fieldWithTooltip,
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Typography, {
                                                            children: label
                                                        }),
                                                        tooltip && /*#__PURE__*/ _jsx(InfoIcon, {
                                                            info: tooltip
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(Controller, {
                                                    control: control,
                                                    name: name,
                                                    render: (props)=>{
                                                        const _connectControllerToTextField = connectControllerToTextField(props), { onChange } = _connectControllerToTextField, rest = _object_without_properties(_connectControllerToTextField, [
                                                            "onChange"
                                                        ]);
                                                        const value = getValues(name);
                                                        const isObject = typeof value === 'object';
                                                        const primitiveValue = isObject ? JSON.stringify(value) : value;
                                                        return /*#__PURE__*/ _jsx(Select, _object_spread_props(_object_spread({
                                                            collapseHelperText: false
                                                        }, rest), {
                                                            value: primitiveValue,
                                                            onChange: (e)=>{
                                                                if (isObject) {
                                                                    e.target.value = JSON.parse(e.target.value);
                                                                }
                                                                onChange(e);
                                                            },
                                                            menuMaxHeight: 300,
                                                            SelectProps: {
                                                                onOpen: ()=>{
                                                                    setLastDayOptionDisabled(getValues('billUpfront'));
                                                                }
                                                            },
                                                            error: error,
                                                            helperText: helperText,
                                                            testId: "".concat(testPrefix, "-").concat(name),
                                                            children: options === null || options === void 0 ? void 0 : options.map((param, i)=>{
                                                                let { value, label, disabled } = param;
                                                                const primitiveValue = typeof value === 'object' ? JSON.stringify(value) : value;
                                                                return /*#__PURE__*/ _jsx(MenuItem, {
                                                                    value: primitiveValue,
                                                                    testId: "".concat(testPrefix, "-").concat(name, "-option-").concat(i),
                                                                    disabled: disabled,
                                                                    children: label
                                                                }, primitiveValue);
                                                            })
                                                        }));
                                                    }
                                                })
                                            ]
                                        }, name);
                                    }
                                } else if (fieldConfig.type === "heading") {
                                    return /*#__PURE__*/ _jsxs(Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "paragraph1",
                                                weight: "normal",
                                                children: fieldConfig.label
                                            }),
                                            /*#__PURE__*/ _jsx("div", {})
                                        ]
                                    }, idx);
                                } else if (fieldConfig.type === "separator") {
                                    return /*#__PURE__*/ _jsx(Fragment, {
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: styles.separator
                                        })
                                    }, idx);
                                } else if (fieldConfig.type === "legalTermsUpload") {
                                    return /*#__PURE__*/ _jsxs(Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Typography, {
                                                children: fieldConfig.label
                                            }),
                                            /*#__PURE__*/ _jsx(Controller, {
                                                control: control,
                                                name: "legalTermsDocumentId",
                                                rules: {
                                                    required: t.errors.pleaseChooseDocument()
                                                },
                                                render: (param)=>{
                                                    let { field, formState } = param;
                                                    var _formState_errors_legalTermsDocumentId;
                                                    return /*#__PURE__*/ _jsx(LegalTermsDropdown, {
                                                        inputRef: field.ref,
                                                        error: formState.isSubmitted && !formState.isSubmitSuccessful ? (_formState_errors_legalTermsDocumentId = formState.errors.legalTermsDocumentId) === null || _formState_errors_legalTermsDocumentId === void 0 ? void 0 : _formState_errors_legalTermsDocumentId.message : undefined,
                                                        onDeleteLegalTerm: onDeleteLegalTerm,
                                                        viewMode: "advanced",
                                                        testId: ProposalDefaultsTID.LegalTermsDropdown(),
                                                        selectedLegalTermId: selectedLegalTerms === null || selectedLegalTerms === void 0 ? void 0 : selectedLegalTerms.id,
                                                        onSelectLegalTerm: (legalTerm)=>{
                                                            var _ref;
                                                            setValue('legalTermsDocumentId', (_ref = legalTerm === null || legalTerm === void 0 ? void 0 : legalTerm.id) !== null && _ref !== void 0 ? _ref : '', {
                                                                shouldValidate: true,
                                                                shouldDirty: true
                                                            });
                                                        }
                                                    });
                                                }
                                            })
                                        ]
                                    }, idx);
                                }
                            }),
                            /*#__PURE__*/ _jsx(IntroductoryMessageEditor, {
                                selectedTemplateFormFieldName: "selectedTemplate",
                                templates: [
                                    {
                                        name: 'introductoryMessages.proposal',
                                        dropdownLabel: t.fields.introductoryMessages.newClientTemplate()
                                    },
                                    {
                                        name: 'introductoryMessages.invitation',
                                        dropdownLabel: t.fields.introductoryMessages.existingClientTemplate()
                                    }
                                ],
                                control: control,
                                formState: formState
                            }),
                            TEMPLATE_VARIABLES && /*#__PURE__*/ _jsxs("div", {
                                id: "custom-tags-section",
                                className: styles.templateVariablesSection,
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph1",
                                        weight: "normal",
                                        children: t.fields.templateVariables.sectionTitle()
                                    }),
                                    /*#__PURE__*/ _jsx("div", {}),
                                    templateVariables.map((variable, index)=>{
                                        const hasLabel = variable.label.trim().length > 0;
                                        const labelError = validateTemplateVariableLabel(variable.label);
                                        const defaultValueError = hasLabel ? validateTemplateVariableDefaultValue(variable.defaultValue) : null;
                                        return /*#__PURE__*/ _jsxs(Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(TextField, {
                                                    value: variable.label,
                                                    onChange: (e)=>updateVariableLabel(index, e.target.value),
                                                    label: t.fields.templateVariables.variableNamePlaceholder(),
                                                    placeholder: t.fields.templateVariables.variableNamePlaceholder(),
                                                    testId: ProposalDefaultsTID.VariableLabel(index),
                                                    error: !!labelError,
                                                    helperText: labelError !== null && labelError !== void 0 ? labelError : undefined
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: styles.templateVariableValueRow,
                                                    children: [
                                                        /*#__PURE__*/ _jsx(TextField, {
                                                            value: variable.defaultValue,
                                                            onChange: (e)=>updateVariableDefaultValue(index, e.target.value),
                                                            label: t.fields.templateVariables.defaultValueLabel(),
                                                            placeholder: t.fields.templateVariables.defaultValuePlaceholder(),
                                                            testId: ProposalDefaultsTID.VariableValue(index),
                                                            error: !!defaultValueError,
                                                            helperText: defaultValueError !== null && defaultValueError !== void 0 ? defaultValueError : undefined,
                                                            disabled: !hasLabel
                                                        }),
                                                        /*#__PURE__*/ _jsx(IconButton, {
                                                            onClick: ()=>removeVariable(index),
                                                            testId: ProposalDefaultsTID.VariableRemove(index),
                                                            type: "button",
                                                            "aria-label": t.fields.templateVariables.removeButton(),
                                                            children: /*#__PURE__*/ _jsx(Trash, {})
                                                        })
                                                    ]
                                                })
                                            ]
                                        }, variable.id);
                                    }),
                                    /*#__PURE__*/ _jsx(TextButton, {
                                        onClick: addVariable,
                                        testId: ProposalDefaultsTID.AddVariable(),
                                        type: "button",
                                        children: /*#__PURE__*/ _jsxs("span", {
                                            className: styles.addVariableContent,
                                            children: [
                                                /*#__PURE__*/ _jsx(PlusCircle, {
                                                    size: 18
                                                }),
                                                t.fields.templateVariables.addButton()
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("div", {})
                                ]
                            }),
                            /*#__PURE__*/ _jsx(IntroVideoField, {
                                introVideo: introVideo,
                                onChange: (introVideo)=>setValue('introVideo', introVideo, {
                                        shouldDirty: true
                                    }),
                                className: styles.video
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(NavigationGuardModal, {
                when: formState.isDirty || isLegalTermsFieldDirty || isTemplateVariablesDirty,
                primaryButtonTestId: "business-settings-confirm-navigation",
                secondaryButtonTestId: "business-settings-keep-editing"
            })
        ]
    });
}
